import { BLACK, WHITE, type Color } from '../primitives/color.js'
import { BISHOP, EMPTY, KNIGHT, PAWN, QUEEN, ROOK, codeColor, codeType, type PieceCode } from '../primitives/piece.js'
import { ALL_SQUARES, isLightSquare } from '../primitives/square.js'
import type { Position } from '../position/position.js'

export interface MaterialCount {
  readonly pawns: number
  readonly knights: number
  /** Split by square colour, because a bishop pair on one colour cannot mate. */
  readonly lightBishops: number
  readonly darkBishops: number
  readonly rooks: number
  readonly queens: number
}

const ZERO: MaterialCount = { pawns: 0, knights: 0, lightBishops: 0, darkBishops: 0, rooks: 0, queens: 0 }

export const countMaterial = (pos: Position, color: Color): MaterialCount => {
  const count = { ...ZERO }
  for (const square of ALL_SQUARES) {
    const code = pos.board[square] as PieceCode
    if (code === EMPTY || codeColor(code) !== color) continue
    switch (codeType(code)) {
      case PAWN:
        count.pawns += 1
        break
      case KNIGHT:
        count.knights += 1
        break
      case BISHOP:
        if (isLightSquare(square)) count.lightBishops += 1
        else count.darkBishops += 1
        break
      case ROOK:
        count.rooks += 1
        break
      case QUEEN:
        count.queens += 1
        break
      default:
        break
    }
  }
  return count
}

const minors = (count: MaterialCount): number => count.knights + count.lightBishops + count.darkBishops
const heavy = (count: MaterialCount): number => count.pawns + count.rooks + count.queens

/**
 * An automatic draw: no sequence of legal moves by either side could produce
 * checkmate.
 *
 * Covers K v K, K+minor v K, and any position where the only pieces left are
 * bishops standing on one colour of square — including bishops of opposite
 * colours belonging to different players, which can never attack each other's
 * escape squares.
 */
export const isInsufficientMaterial = (pos: Position): boolean => {
  const white = countMaterial(pos, WHITE)
  const black = countMaterial(pos, BLACK)
  if (heavy(white) > 0 || heavy(black) > 0) return false

  const totalMinors = minors(white) + minors(black)
  if (totalMinors <= 1) return true

  const knights = white.knights + black.knights
  if (knights > 0) return false

  const light = white.lightBishops + black.lightBishops
  const dark = white.darkBishops + black.darkBishops
  return light === 0 || dark === 0
}

/**
 * Could `color` still deliver checkmate, given cooperative play from the
 * opponent? This is the question the timeout rule asks: a player who flags
 * against a bare king does not lose, the game is drawn.
 *
 * A lone minor piece cannot mate. Two minors can, unless they are two bishops
 * on the same colour of square.
 */
export const canMate = (pos: Position, color: Color): boolean => {
  const count = countMaterial(pos, color)
  if (heavy(count) > 0) return true
  if (minors(count) < 2) return false
  if (count.knights > 0) return true
  return count.lightBishops > 0 && count.darkBishops > 0
}

/** Standard values, used only for the captured-material advantage in the UI. */
export const materialValue = (count: MaterialCount): number =>
  count.pawns + 3 * (count.knights + count.lightBishops + count.darkBishops) + 5 * count.rooks + 9 * count.queens
